/**
 * SOOID product page.
 *
 * Drives the gallery and sticky mobile bar. Purchase state and all variant /
 * selling-plan validation are shared with the homepage in sooid-purchase.js.
 */
class SooidPdp extends HTMLElement {
  constructor() {
    super();
    this.mainImage = this.querySelector('[data-gallery-main] img');
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));

    this.setupSlideshow();
    this.showDeliveryDates();

    // Every image slide enlarges in the lightbox right where it sits: nothing on the page moves, so
    // nobody has to scroll back up to see what they clicked. Video slides keep their play control.
    this.indexSlides();
    this.lightboxIndex = -1;

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keydown', (event) => {
      const slide = event.target.closest?.('[data-lightbox]');
      if (slide && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.openLightbox(Number(slide.dataset.lightbox));
      }
    });
  }

  /**
   * Turns "5–8 business days" into calendar dates ("Get it Thu, Oct 2 – Tue, Oct 7"), counting
   * business days from today and skipping weekends. Same window as the shipping zone promises.
   */
  showDeliveryDates() {
    const line = this.querySelector('[data-delivery]');
    const when = line?.querySelector('[data-delivery-when]');
    const min = Number(line?.dataset.min);
    const max = Number(line?.dataset.max);
    if (!when || !min || !max || max < min) return;

    const addBusinessDays = (days) => {
      const date = new Date();
      while (days > 0) {
        date.setDate(date.getDate() + 1);
        if (date.getDay() !== 0 && date.getDay() !== 6) days -= 1;
      }
      return date;
    };
    const format = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    when.textContent = `Get it ${format.format(addBusinessDays(min))} – ${format.format(addBusinessDays(max))}`;
  }

  /** Builds thumbnail buttons from the real slide media, then shows exactly one slide at a time. */
  setupSlideshow() {
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slides] > .sooid-gal__slide'));
    this.slideshowThumbs = this.querySelector('[data-gallery-thumbs]');
    if (!this.slides.length || !this.slideshowThumbs) return;

    const fragment = document.createDocumentFragment();
    this.slides.forEach((slide, index) => {
      const image = slide.querySelector('img');
      const video = slide.querySelector('video');
      const thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = `sooid-gal__thumb${video ? ' sooid-gal__thumb--video' : ''}`;
      thumb.dataset.galleryThumb = String(index);
      thumb.setAttribute('aria-current', String(index === 0));
      thumb.setAttribute('aria-label', `Show media ${index + 1} of ${this.slides.length}`);

      const preview = document.createElement('img');
      const previewSource = image?.currentSrc || image?.src || video?.poster || '';
      preview.src = previewSource.replace(/([?&])width=\d+/, '$1width=160');
      preview.alt = '';
      preview.loading = 'lazy';
      preview.width = 160;
      preview.height = 160;
      thumb.appendChild(preview);
      if (video) {
        const play = document.createElement('span');
        play.className = 'sooid-gal__play';
        play.setAttribute('aria-hidden', 'true');
        play.textContent = '▶';
        thumb.appendChild(play);
      }
      fragment.appendChild(thumb);
      slide.classList.toggle('is-active', index === 0);
      slide.hidden = index !== 0;
    });
    this.slideshowThumbs.appendChild(fragment);
  }

  /** Numbers the image slides in DOM order and puts the magnifier cue on the lead photo. */
  indexSlides() {
    // Section setting "Photos enlarge on click" off: no magnifier, no lightbox, the slides stay plain figures.
    if (this.dataset.zoom === 'false') { this.lightboxSlides = []; return; }
    this.lightboxSlides = Array.from(this.querySelectorAll('.sooid-gal__slide')).filter((slide) => slide.querySelector('img') && !slide.classList.contains('sooid-gal__slide--video'));
    this.lightboxSlides.forEach((slide, index) => {
      slide.dataset.lightbox = String(index);
      slide.tabIndex = 0;
      slide.setAttribute('role', 'button');
      slide.setAttribute('aria-label', `Enlarge image ${index + 1} of ${this.lightboxSlides.length}`);
    });
    // Decorative only: the slide itself is the button, so the magnifier is not a second control inside it.
    const lead = this.querySelector('.sooid-gal__slide--lead[data-lightbox]');
    if (!lead) return;
    const zoom = document.createElement('span');
    zoom.className = 'sooid-gal__zoom';
    zoom.setAttribute('aria-hidden', 'true');
    zoom.innerHTML = '<svg viewBox="0 0 24 24"><path d="M10 4a6 6 0 1 1 0 12 6 6 0 0 1 0-12zm0 2a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm4.9 8.5 4.6 4.6-1.4 1.4-4.6-4.6zM9 7h2v2h2v2h-2v2H9v-2H7V9h2z" fill="currentColor"/></svg>';
    lead.appendChild(zoom);
  }

  onClick(event) {
    const slideshowThumb = event.target.closest('[data-gallery-thumb]');
    if (slideshowThumb) return this.selectSlide(Number(slideshowThumb.dataset.galleryThumb));
    const thumb = event.target.closest('[data-thumb]');
    if (thumb) return this.selectThumb(thumb);
    const slide = event.target.closest('[data-lightbox]');
    if (slide) this.openLightbox(Number(slide.dataset.lightbox));
  }

  selectSlide(index) {
    if (!Number.isInteger(index) || index < 0 || index >= this.slides.length) return;
    this.slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active', slideIndex === index);
      slide.hidden = slideIndex !== index;
      const video = slide.querySelector('video');
      if (video && slideIndex !== index) video.pause();
    });
    this.slideshowThumbs.querySelectorAll('[data-gallery-thumb]').forEach((thumb, thumbIndex) => {
      thumb.setAttribute('aria-current', String(thumbIndex === index));
    });
    const activeVideo = this.slides[index].querySelector('video');
    if (activeVideo) {
      activeVideo.muted = true;
      activeVideo.setAttribute('muted', '');
      const played = activeVideo.play();
      if (played) played.catch(() => {});
    }
  }

  /* ---------- lightbox ---------- */

  lightbox() {
    if (this.lightboxEl) return this.lightboxEl;
    const el = document.createElement('div');
    el.className = 'sooid-lightbox';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Enlarged product image');
    el.hidden = true;
    el.innerHTML = `
      <button type="button" class="sooid-lightbox__close" data-lightbox-close aria-label="Close">&times;</button>
      <button type="button" class="sooid-lightbox__nav sooid-lightbox__nav--prev" data-lightbox-prev aria-label="Previous image">&#8249;</button>
      <figure class="sooid-lightbox__figure"><img class="sooid-lightbox__img" alt=""></figure>
      <button type="button" class="sooid-lightbox__nav sooid-lightbox__nav--next" data-lightbox-next aria-label="Next image">&#8250;</button>
      <p class="sooid-lightbox__count" aria-live="polite"></p>`;
    el.addEventListener('click', (event) => {
      if (event.target === el || event.target.closest('[data-lightbox-close]')) return this.closeLightbox();
      if (event.target.closest('[data-lightbox-prev]')) return this.stepLightbox(-1);
      if (event.target.closest('[data-lightbox-next]')) return this.stepLightbox(1);
    });
    el.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') return this.closeLightbox();
      if (event.key === 'ArrowLeft') return this.stepLightbox(-1);
      if (event.key === 'ArrowRight') return this.stepLightbox(1);
    });
    document.body.appendChild(el);
    this.lightboxEl = el;
    return el;
  }

  openLightbox(index) {
    const el = this.lightbox();
    this.lightboxReturn = this.lightboxSlides[index];
    this.showLightbox(index);
    el.hidden = false;
    document.documentElement.classList.add('sooid-lightbox-open');
    el.querySelector('[data-lightbox-close]').focus();
  }

  showLightbox(index) {
    const total = this.lightboxSlides.length;
    if (!total) return;
    this.lightboxIndex = (index + total) % total;
    const source = this.lightboxSlides[this.lightboxIndex].querySelector('img');
    const img = this.lightboxEl.querySelector('.sooid-lightbox__img');
    img.src = source.currentSrc || source.src;
    img.alt = source.alt || '';
    this.lightboxEl.querySelector('.sooid-lightbox__count').textContent = `${this.lightboxIndex + 1} / ${total}`;
    this.lightboxEl.querySelectorAll('.sooid-lightbox__nav').forEach((nav) => { nav.hidden = total < 2; });
  }

  stepLightbox(direction) {
    if (this.lightboxIndex < 0) return;
    this.showLightbox(this.lightboxIndex + direction);
  }

  closeLightbox() {
    if (!this.lightboxEl || this.lightboxEl.hidden) return;
    this.lightboxEl.hidden = true;
    document.documentElement.classList.remove('sooid-lightbox-open');
    this.lightboxReturn?.focus({ preventScroll: true });
    this.lightboxIndex = -1;
  }

  selectThumb(thumb) {
    this.thumbs.forEach((t) => t.setAttribute('aria-current', String(t === thumb)));
    const main = this.querySelector('[data-gallery-main]');
    if (!main) return;
    if (thumb.dataset.video) {
      if (!this.galleryVideo) {
        this.galleryVideo = document.createElement('video');
        // Attributes, not properties: Chrome's autoplay policy only honours
        // the muted *attribute*, and an explicit source type sidesteps the
        // CDN serving application/mp4 instead of video/mp4.
        ['muted', 'controls', 'playsinline'].forEach((a) => this.galleryVideo.setAttribute(a, ''));
        this.galleryVideo.preload = 'metadata';
        this.galleryVideo.muted = true;
        this.videoSource = document.createElement('source');
        this.videoSource.type = 'video/mp4';
        this.galleryVideo.appendChild(this.videoSource);
      }
      this.galleryVideo.poster = thumb.dataset.poster || '';
      if (this.videoSource.src !== thumb.dataset.video) {
        this.videoSource.src = thumb.dataset.video;
        this.galleryVideo.load();
      }
      if (this.mainImage) this.mainImage.hidden = true;
      if (!this.galleryVideo.isConnected) main.appendChild(this.galleryVideo);
      this.galleryVideo.hidden = false;
      const played = this.galleryVideo.play();
      if (played) played.catch(() => {});
      return;
    }

    if (this.galleryVideo) {
      this.galleryVideo.pause && this.galleryVideo.pause();
      this.galleryVideo.hidden = true;
    }
    const full = thumb.dataset.full;
    if (full && this.mainImage) {
      this.mainImage.hidden = false;
      this.mainImage.src = full;
      this.mainImage.srcset = '';
    }
  }

}

customElements.define('sooid-pdp', SooidPdp);

/**
 * Reveals the sticky bar once the main buy button has scrolled out of view,
 * and hands its tap back to the real form so there is one source of truth.
 */
class SooidSticky extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sticky-add]');
    if (this.button) {
      // Always buys the pack the bar names (the pre-selected one until the shopper changes it); the bar
      // used to scroll to the pack picker when tapped above it, which the owner dropped on 2026-10-08.
      this.button.addEventListener('click', () => {
        const form = document.querySelector('[data-pdp-form]');
        if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
      });
    }

    const anchor = document.querySelector('[data-atc]');
    if (!anchor || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.dataset.visible = String(!entry.isIntersecting);
        if (this.button) this.button.textContent = this.button.dataset.addLabel || 'BUY NOW';
      },
      { rootMargin: '0px' }
    );
    this.observer.observe(anchor);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}
customElements.define('sooid-sticky', SooidSticky);
